import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Box, ButtonGroup, Grid, GridProps, LinearProgress, Stack, Typography } from '@mui/material';

import { useQuery } from '@tanstack/react-query';

import { venueGetOfferStatisticsApi } from '../../api/offers/venueGetOfferStatisticsApi';
import { venueListOffersApi } from '../../api/offers/venueListOffersApi';
import Button from '../../components/Button/Button';
import ChipOptions from '../../components/ChipOptions/ChipOptions';
import { ShopifyProductsDialog } from '../../components/Dialogs/ShopifyProducts/ShopifyProductsDialog';
import PageTitle from '../../components/PageTitle/PageTitle';
import OffersTable from '../../components/Tables/Offers/OffersTable';
import { enableShopifyVenueID, environment } from '../../config';
import { OfferCategory, OfferStatus, VenueListOffersFilter } from '../../graphql/API';
import TikTokOfferCard from '../../components/TikTokOfferBanner/TikTokOfferCard';
import useAccessRequestQuery from '../../hooks/reactQuery/useAccessRequestQuery';
import { useListOfferQuery } from '../../hooks/reactQuery/useOfferQuery';
import { useFilter } from '../../hooks/useFilter';
import { useAppSelector } from '../../redux/hooks';
import icons from '../../themes/icons';
import { VENUE_MANUAL_TAGS } from '../../utils/venue-tags';
import AddProduct from './components/AddProduct';
import { NewProduct } from './components/NewProduct';
import ProductCard from './components/ProductCard';
import ProductCardSkeleton from './components/ProductCardSkeleton';

type ProductPageView = 'grid' | 'list';

const VIEW_STORAGE_KEY = 'productPageView';

const commonGridProps = (key: string): GridProps => ({
  key,
  item: true,
  xs: 6,
  md: 4,
  lg: 3,
  xl: 2.4,
});

const Products: React.FC = () => {
  const navigate = useNavigate();
  const venue = useAppSelector((state) => state.venue.value);
  const isOnlineBrand = venue?.tags?.includes(VENUE_MANUAL_TAGS.Online);

  const [filters, setFilters] = useFilter<Partial<VenueListOffersFilter>>({
    category: OfferCategory.product,
    status: OfferStatus.active,
  });
  const [productsDialog, setProductsDialog] = React.useState(false);

  const [view, setView] = React.useState<ProductPageView>(
    () => (localStorage.getItem(VIEW_STORAGE_KEY) as ProductPageView) || 'grid',
  );

  const handleViewChange = React.useCallback((nextView: ProductPageView) => {
    setView(nextView);
    localStorage.setItem(VIEW_STORAGE_KEY, nextView);
  }, []);

  const { data, isFetching, isLoading } = useListOfferQuery(filters);

  // Count how many times each product has been requested (access requests),
  // keyed by offerID. Only fetched in list view, where the column is shown.
  const { data: allAccessRequests } = useAccessRequestQuery({}, view !== 'list', 1000);
  const requestCounts = React.useMemo(() => {
    const counts: Record<string, number> = {};
    (allAccessRequests || []).forEach((req) => {
      if (req.offerID !== null && req.offerID !== undefined) {
        const key = String(req.offerID);
        counts[key] = (counts[key] || 0) + 1;
      }
    });
    return counts;
  }, [allAccessRequests]);
  const { data: offerStatisticsData, isFetching: venueGetOfferStatisticsIsFetching } = useQuery({
    queryKey: ['venueGetOfferStatistics', venue?.id || ''],
    queryFn: async ({ queryKey }) => {
      const [, venueId] = queryKey as [string, string];

      return venueGetOfferStatisticsApi({
        venueID: Number(venueId),
        category: OfferCategory.product,
      });
    },
    enabled: Boolean(venue?.id),
  });

  React.useEffect(() => {
    if (!isOnlineBrand) navigate('/offers');
  }, [isOnlineBrand, navigate]);

  // Backend's `totalDeletedOffers` in statistics can be unreliable, so we fetch
  // the cancelled+deleted offers explicitly and count them client-side.
  const { data: cancelledLikeOffers } = useQuery({
    queryKey: ['cancelled-deleted-offers-count', venue?.id],
    queryFn: () =>
      venueListOffersApi({
        venueID: venue?.id || '',
        statuses: [OfferStatus.cancelled, OfferStatus.deleted],
        category: OfferCategory.product,
        limit: 500,
        offset: 0,
      }),
    enabled: Boolean(venue?.id),
  });

  const totalCancelledIncludingDeleted = cancelledLikeOffers?.length ?? 0;

  const totalProducts =
    (offerStatisticsData?.totalActiveOffers || 0) +
    (offerStatisticsData?.totalPendingOffers || 0) +
    totalCancelledIncludingDeleted;

  return (
    <Box
      padding={4}
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
        flex: 1,
        position: 'relative',
      }}
    >
      <Box>
        <PageTitle
          title='Your Products'
          sideComponent={
            <>
              {String(enableShopifyVenueID) === String(venue?.id) ? (
                <Button
                  variant='outlined'
                  color='primary'
                  onClick={() => {
                    setProductsDialog(true);
                  }}
                >
                  List Shopify products
                </Button>
              ) : null}
            </>
          }
        />

        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          flexWrap='wrap'
          gap={2}
          mt={2}
        >
          <ChipOptions
            name='offerStatus'
            options={[
              {
                label: `All`,
                value: '',
              },
              {
                label: `Active (${offerStatisticsData?.totalActiveOffers ?? '0'})`,
                value: OfferStatus.active,
              },
              {
                label: `In Review (${offerStatisticsData?.totalPendingOffers ?? '0'})`,
                value: OfferStatus.pending,
              },
              {
                label: `Cancelled (${totalCancelledIncludingDeleted})`,
                value: OfferStatus.cancelled,
              }
            ]}
            defaultValue={
              filters.statuses?.includes(OfferStatus.cancelled)
                ? OfferStatus.cancelled
                : (filters.status ?? '')
            }
            onChange={({ value: v }) =>
              setFilters((prev) => ({
                ...prev,
                status: v === OfferStatus.cancelled || !v ? undefined : (v as OfferStatus),
                statuses:
                  v === OfferStatus.cancelled
                    ? [OfferStatus.cancelled, OfferStatus.deleted]
                    : undefined,
              }))
            }
          />

          <Stack direction='row' alignItems='center' gap={2} sx={{ flexShrink: 0 }}>
            {view === 'list' && (
              <Button
                component={Link}
                to='/products/create'
                variant='contained'
                startIcon={<icons.AddIcon />}
                sx={{ borderRadius: '8px', whiteSpace: 'nowrap' }}
              >
                List a product
              </Button>
            )}

            <ButtonGroup variant='outlined' size='medium' sx={{ flexShrink: 0 }}>
              <Button
                sx={{
                  border: '1px solid #EFEEEE',
                  ...(view === 'grid' && { color: '#765BC6', backgroundColor: '#F1EFF9' }),
                }}
                onClick={() => handleViewChange('grid')}
              >
                <icons.GridIconV2 />
              </Button>
              <Button
                sx={{
                  border: '1px solid #EFEEEE',
                  ...(view === 'list' && { color: '#765BC6', backgroundColor: '#F1EFF9' }),
                }}
                onClick={() => handleViewChange('list')}
              >
                <icons.ListIconV2 />
              </Button>
            </ButtonGroup>
          </Stack>
        </Stack>

        <Box mt={4}>
          {((!isLoading && isFetching) || venueGetOfferStatisticsIsFetching) && (
            <LinearProgress sx={{ mb: 1 }} />
          )}
        </Box>
      </Box>

      <Box>
        {isLoading || venueGetOfferStatisticsIsFetching ? (
          <Grid container spacing={2}>
            {[...Array(4)].map((_, i) => (
              <Grid {...commonGridProps(`grid-item-${i}`)}>
                <ProductCardSkeleton />
              </Grid>
            ))}
          </Grid>
        ) : view === 'list' ? (
          <OffersTable data={data} variant='product' requestCounts={requestCounts} />
        ) : !filters.status && !filters.statuses?.length ? (
          <Stack spacing={5}>
            {(
              [
                { key: OfferStatus.active, label: 'Active' },
                { key: OfferStatus.pending, label: 'In Review' },
                { key: OfferStatus.cancelled, label: 'Cancelled' },
              ] as const
            ).map(({ key, label }) => {
              const items = data.filter((d) =>
                key === OfferStatus.cancelled
                  ? d.status === OfferStatus.cancelled || d.status === OfferStatus.deleted
                  : d.status === key,
              );
              const isActive = key === OfferStatus.active;
              if (items.length === 0 && !isActive) return null;

              return (
                <Box key={key}>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#1A1C1E',
                      mb: 2,
                    }}
                  >
                    {label} ({items.length})
                  </Typography>
                  <Grid container spacing={2}>
                    {isActive && (
                      <>
                        <Grid {...commonGridProps('add-offer')}>
                          {totalProducts === 0 ? <AddProduct /> : <NewProduct />}
                        </Grid>
                        <Grid {...commonGridProps('tiktok-promo')}>
                          <TikTokOfferCard />
                        </Grid>
                      </>
                    )}
                    {items.map((x) => (
                      <Grid {...commonGridProps(x.id)}>
                        <ProductCard data={x} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Grid container spacing={2}>
            <Grid {...commonGridProps('add-offer')}>
              {totalProducts === 0 ? <AddProduct /> : <NewProduct />}
            </Grid>
            <Grid {...commonGridProps('tiktok-promo')}>
              <TikTokOfferCard />
            </Grid>
            {data.map((x) => (
              <Grid {...commonGridProps(x.id)}>
                <ProductCard data={x} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <ShopifyProductsDialog
        open={productsDialog}
        onClose={() => {
          setProductsDialog(false);
        }}
      />
    </Box>
  );
};

export default Products;
